import { IconID } from '@hub/common/Icon/Icon';
import type { Contact } from '@hub/models/api/contact-area-type.types';
import { Link } from '@hub/common/Link/Link';
import { useTranslation } from 'next-i18next';
import { getLocale } from '@hub/libs/constants';
import { EXTERNAL_LINKS } from '@hub/libs/redirect.helper';
import classNames from 'classnames';
import styles from '../ContactPersonBox/ContactPersonBox.module.scss';
import { useRouter } from 'next/router';

export interface ContactPersonBoxContactAreaTypeProps {
  contacts: Contact[];
}

export const ContactPersonBoxContactAreaType = ({ contacts }: ContactPersonBoxContactAreaTypeProps) => {
  const { t } = useTranslation('layout/contactPersonBox');
  const router = useRouter();
  const locale = getLocale(router);
  return (
    <div
      data-testid="contact-area-type"
      className={classNames(styles.box, 'border', 'border-gray-200', 'absolute', 'px-12', 'bg-white', 'z-50')}
    >
      {contacts && contacts?.length > 0
        ? (contacts as unknown as Contact[]).map(contact => (
            <div className="flex gap-4 flex-col px-12 py-12 border-b border-gray-100" key={contact.name}>
              <h6 className="mb-4 text-xl text-gray-500">{contact.companyName}</h6>
              <div className="mb-2 text-md text-gray-500">{contact.address1}</div>
              <div className="mb-2 text-md text-gray-500">{contact.address2}</div>
              {contact.address3 && <div className="mb-2 text-md text-gray-500">{contact.address3}</div>}
              {contact.phone && (
                <div className="mb-2 text-gray-500 text-xs text-align-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className="inline-block"
                    style={{ width: '1em', height: '1em', verticalAlign: '-0.125em' }}
                  >
                    <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z" />
                  </svg>
                  <span className="ml-2">{contact.phone}</span>
                </div>
              )}
              {contact.fax && (
                <div className="mb-4 text-gray-500 text-xs text-align-center">
                  {/* font awesome fas fa-fax icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className="inline-block"
                    style={{ width: '1em', height: '1em', verticalAlign: '-0.125em' }}
                  >
                    <path d="M128 64v96h64V64H386.7L416 93.3V160h64V93.3c0-17-6.7-33.3-18.7-45.3L432 18.7C420 6.7 403.7 0 386.7 0H192c-35.3 0-64 28.7-64 64zM0 160V480c0 17.7 14.3 32 32 32H64c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H32c-17.7 0-32 14.3-32 32zm480 32H128V480c0 17.7 14.3 32 32 32H480c17.7 0 32-14.3 32-32V224c0-17.7-14.3-32-32-32zM256 320c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32zm160-32c0 17.7-14.3 32-32 32s-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32zM384 448c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32zm-96-32c0 17.7-14.3 32-32 32s-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32z" />
                  </svg>
                  <span className="ml-2">{contact.fax}</span>
                </div>
              )}
              <Link
                data-testid="link-message"
                text={t('CONTACT_PERSON_BOX.WRITE_MESSAGE')}
                icon={IconID.ARROW_RIGHT}
                href={
                  contact?.contactFormUrl
                    ? contact.contactFormUrl
                    : EXTERNAL_LINKS.CONTACT_PERSON.CONTACT_FORM(contact.id, locale)
                }
                external
                bigIcon={false}
                iconPrimary
                className="text-xs"
                primary={false}
              />
            </div>
          ))
        : null}
      <Link
        text={t('CONTACT_PERSON_BOX.SEE_ALL_CONTACT')}
        icon={IconID.ARROW_RIGHT}
        bigIcon={false}
        href={EXTERNAL_LINKS.CONTACT_PERSON.SEE_ALL_CONTACT(locale)}
        className="text-xs my-12 px-12"
        primary
      />
    </div>
  );
};
